<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Visit } from '../services/api';
import { useVisits } from '../composables/useVisits';
import { formatVisitAt } from '../utils/date';
import ConfirmDialog from './ConfirmDialog.vue';

// Owns the labels, the pending flag and the call itself, so the list and the detail
// page only have to say which visit and what to do afterwards.
const props = defineProps<{ visit: Visit | null }>();
const emit = defineEmits<{ close: []; cancelled: [] }>();

const { t } = useI18n();
const { cancelVisit } = useVisits();

const isCancelling = ref(false);
const error = ref<string | null>(null);

watch(
  () => props.visit,
  () => {
    error.value = null;
  },
);

const message = computed(() =>
  props.visit
    ? t('visits.cancel_confirm', {
        client: props.visit.client?.name ?? '—',
        date: formatVisitAt(props.visit.visit_at),
      })
    : '',
);

const handleConfirm = async (): Promise<void> => {
  if (!props.visit) return;

  isCancelling.value = true;
  const result = await cancelVisit(props.visit.id);
  isCancelling.value = false;

  if (!result.ok) {
    error.value = result.message;
    return;
  }

  emit('cancelled');
  emit('close');
};
</script>

<template>
  <ConfirmDialog
    :open="visit !== null"
    :title="t('visits.cancel_title')"
    :message="error ?? message"
    :confirm-label="t('visits.cancel_submit')"
    :cancel-label="t('common.cancel')"
    :is-loading="isCancelling"
    :is-error="error !== null"
    @confirm="handleConfirm"
    @cancel="emit('close')"
  />
</template>
